import {
  RULE_BOX_MAX_ITEMS,
  RULE_BOX_MIN_ITEMS,
  type RuleBoxFacets,
  SHOPIFY_CATEGORY_VALUES_SCOPE,
  SHOPIFY_LOCATIONS_SCOPE,
} from "@loot-os/api-contract";
import type { RuleBoxForm, RuleBoxFormField } from "@loot-os/api-contract/shopifyRuleBoxForm";
import { dollars, textChoices } from "@loot-os/api-contract/shopifyRuleBoxForm";
import { CategoryFilters } from "./CategoryFilters";
import { ChoicePicker } from "./ChoicePicker";
import { fieldValue } from "./fieldValue";
import { LocationChoices } from "./LocationChoices";
import { OptionFilters } from "./OptionFilters";
import { PreviewPanel, type PreviewState } from "./PreviewPanel";
import { useRuleFilterScopes } from "./ruleFilterScopes";

/**
 * The builder's rules page: which of the shop's products qualify, by collection, tag, vendor,
 * product type, category attribute, variant option, price and the location stocking them, beside
 * the live estimate for those rules.
 */
export function RulesStep({
  form,
  onChange,
  errors,
  facets,
  facetsLoading,
  preview,
}: {
  form: RuleBoxForm;
  onChange: (patch: Partial<RuleBoxForm>) => void;
  errors: Partial<Record<RuleBoxFormField, string>>;
  facets: RuleBoxFacets | undefined;
  facetsLoading: boolean;
  preview: PreviewState;
}) {
  const empty = facets !== undefined && facets.minPriceCents === null;
  const scopes = useRuleFilterScopes();
  return (
    <s-stack gap="base">
      <s-paragraph>
        Say which of your products can come out of this box, and we'll build it for you from the
        matching products in stock, priced so you keep your margin. Every day we sync your store and
        rebuild the box, so sold-out products drop out and new matching ones join on their own.
      </s-paragraph>
      {empty && (
        <s-banner tone="info">
          No products yet. Open the Products tab and click "Sync now" to bring in your store's
          products.
        </s-banner>
      )}
      {facetsLoading && <s-text color="subdued">Loading your tags and vendors…</s-text>}
      <s-heading>Products</s-heading>
      <ChoicePicker
        label="Collections"
        details="A product in any one of these collections qualifies. Leave empty for any collection."
        placeholder="Search your collections"
        options={facets?.collections ?? []}
        chosen={form.collections}
        freeText={false}
        noun="collections"
        missingLabel="Deleted collection"
        onChange={(collections) => onChange({ collections })}
      />
      <ChoicePicker
        label="Products tagged"
        details="A product with any one of these tags qualifies. Leave empty for any tag."
        placeholder="Start typing a tag, e.g. rolex"
        options={textChoices(facets?.tags ?? [])}
        chosen={form.includeTags}
        freeText
        noun="tags"
        onChange={(includeTags) => onChange({ includeTags })}
      />
      <ChoicePicker
        label="Leave out products tagged"
        details="A product with any one of these tags never goes in."
        placeholder="used"
        options={textChoices(facets?.tags ?? [])}
        chosen={form.excludeTags}
        freeText
        noun="tags"
        onChange={(excludeTags) => onChange({ excludeTags })}
      />
      <ChoicePicker
        label="Vendors"
        details="Only products from these vendors. Leave empty for any vendor."
        placeholder="Search your vendors"
        options={textChoices(facets?.vendors ?? [])}
        chosen={form.vendors}
        freeText={false}
        noun="vendors"
        onChange={(vendors) => onChange({ vendors })}
      />
      <ChoicePicker
        label="Product types"
        details="Only products of these types. Leave empty for any type."
        placeholder="Search your product types"
        options={textChoices(facets?.productTypes ?? [])}
        chosen={form.productTypes}
        freeText={false}
        noun="product types"
        onChange={(productTypes) => onChange({ productTypes })}
      />
      {facets && (
        <>
          <s-heading>Category attributes</s-heading>
          <CategoryFilters
            facets={facets}
            filters={form.categoryAttributes}
            pending={scopes.pending === SHOPIFY_CATEGORY_VALUES_SCOPE}
            onAllow={() => void scopes.request(SHOPIFY_CATEGORY_VALUES_SCOPE)}
            onChange={(categoryAttributes) => onChange({ categoryAttributes })}
          />
          <s-heading>Variants</s-heading>
          <OptionFilters
            facets={facets}
            filters={form.options}
            onChange={(options) => onChange({ options })}
          />
        </>
      )}
      <s-grid gridTemplateColumns="1fr 1fr" gap="base">
        <s-text-field
          label="Lowest product price"
          prefix="$"
          inputMode="decimal"
          placeholder={
            facets?.minPriceCents != null ? String(facets.minPriceCents / 100) : "No minimum"
          }
          details="Leave empty for no minimum."
          value={form.minPrice}
          error={errors.minPrice}
          onInput={(e) => onChange({ minPrice: fieldValue(e) })}
        />
        <s-text-field
          label="Highest product price"
          prefix="$"
          inputMode="decimal"
          placeholder={
            facets?.maxPriceCents != null ? String(facets.maxPriceCents / 100) : "No maximum"
          }
          details="Leave empty for no maximum."
          value={form.maxPrice}
          error={errors.maxPrice}
          onInput={(e) => onChange({ maxPrice: fieldValue(e) })}
        />
      </s-grid>
      {facets?.minPriceCents != null && facets.maxPriceCents != null && (
        <s-text color="subdued">
          Your products range from {dollars(facets.minPriceCents)} to{" "}
          {dollars(facets.maxPriceCents)}.
        </s-text>
      )}
      {facets && (
        <>
          <s-heading>Fulfillment</s-heading>
          <LocationChoices
            facets={facets}
            chosen={form.locations}
            pending={scopes.pending === SHOPIFY_LOCATIONS_SCOPE}
            onAllow={() => void scopes.request(SHOPIFY_LOCATIONS_SCOPE)}
            onChange={(locations) => onChange({ locations })}
          />
        </>
      )}
      <s-heading>Box size</s-heading>
      <s-grid gridTemplateColumns="1fr 1fr" gap="base">
        <s-number-field
          label="Fewest items"
          inputMode="numeric"
          min={RULE_BOX_MIN_ITEMS}
          max={RULE_BOX_MAX_ITEMS}
          step={1}
          placeholder={String(RULE_BOX_MIN_ITEMS)}
          details="With fewer matching products in stock, the box shows sold out. Leave empty for no minimum."
          value={form.minItems}
          error={errors.minItems}
          onInput={(e) => onChange({ minItems: fieldValue(e) })}
        />
        <s-number-field
          label="Most items"
          inputMode="numeric"
          min={RULE_BOX_MIN_ITEMS}
          max={RULE_BOX_MAX_ITEMS}
          step={1}
          placeholder={String(RULE_BOX_MAX_ITEMS)}
          details="With more matching products, we keep a spread of them by price. Leave empty for no maximum."
          value={form.maxItems}
          error={errors.maxItems}
          onInput={(e) => onChange({ maxItems: fieldValue(e) })}
        />
      </s-grid>
      <PreviewPanel state={preview} priceTarget={{ form, errors, onChange }} />
    </s-stack>
  );
}
